import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ReferenceCard from "./ReferenceCard";
import "./QACard.css";

function ThumbUp({ filled }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      {filled ? (
        <path d="M9,21H5C3.9,21 3,20.1 3,19V9C3,7.9 3.9,7 5,7H9V3.5C9,2.7 9.7,2 10.5,2C10.78,2 11.06,2.11 11.28,2.31L11.29,2.3L18,9C18.37,9.37 18.6,9.88 18.6,10.43V10.5C18.6,10.78 18.53,11.03 18.41,11.24L14.47,18.84C14.18,19.37 13.62,19.7 13,19.7H9.33C9.14,19.7 9,19.86 9,20.05V21 M22,9V21H17V9H22Z" />
      ) : (
        <path d="M2,20H4V8H2V20M22,9C22,7.9 21.1,7 20,7H14.31L15.3,3.11L15.33,2.88C15.33,2.53 15.18,2.23 14.96,2L14,1L8.35,6.65C8.13,6.87 8,7.18 8,7.5V19.5C8,20.05 8.45,20.5 9,20.5H19C19.79,20.5 20.54,20.14 21,19.5L22,17.89C22.07,17.77 22.1,17.64 22.1,17.5V9Z" />
      )}
    </svg>
  );
}
function ThumbDown({ filled }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style={{ transform: "rotate(180deg)" }}>
      {filled ? (
        <path d="M9,21H5C3.9,21 3,20.1 3,19V9C3,7.9 3.9,7 5,7H9V3.5C9,2.7 9.7,2 10.5,2C10.78,2 11.06,2.11 11.28,2.31L11.29,2.3L18,9C18.37,9.37 18.6,9.88 18.6,10.43V10.5C18.6,10.78 18.53,11.03 18.41,11.24L14.47,18.84C14.18,19.37 13.62,19.7 13,19.7H9.33C9.14,19.7 9,19.86 9,20.05V21 M22,9V21H17V9H22Z" />
      ) : (
        <path d="M2,20H4V8H2V20M22,9C22,7.9 21.1,7 20,7H14.31L15.3,3.11L15.33,2.88C15.33,2.53 15.18,2.23 14.96,2L14,1L8.35,6.65C8.13,6.87 8,7.18 8,7.5V19.5C8,20.05 8.45,20.5 9,20.5H19C19.79,20.5 20.54,20.14 21,19.5L22,17.89C22.07,17.77 22.1,17.64 22.1,17.5V9Z" />
      )}
    </svg>
  );
}

export default function QACard({ question, answerObj, isLoading, isActive, apiBase }) {
  const ref = useRef(null);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (ref.current) {
      setTimeout(() => ref.current.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, []);

  useEffect(() => {
    if (isLoading || !answerObj?.answer) {
      setDisplayedText("");
      setIsTyping(false);
      return;
    }
    const full = answerObj.answer;
    const tick = 18;
    const maxMs = 2500;
    const step = Math.max(1, Math.ceil(full.length / (maxMs / tick)));
    let idx = 0;
    setDisplayedText("");
    setIsTyping(true);
    const id = setInterval(() => {
      idx += step;
      if (idx >= full.length) {
        clearInterval(id);
        setDisplayedText(full);
        setIsTyping(false);
      } else {
        setDisplayedText(full.slice(0, idx));
      }
    }, tick);
    return () => clearInterval(id);
  }, [answerObj, isLoading]);

  async function handleFeedback(type) {
    const next = feedback === type ? null : type;
    setFeedback(next);
    setToast(next ? "Mulțumim pentru feedback!" : "Feedback eliminat");
    setTimeout(() => setToast(""), 2500);

    if (!answerObj?.id || !next) return;
    try {
      await fetch(`${apiBase}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message_id: answerObj.id,
          rating: next === "up" ? 5 : 1,
        }),
      });
    } catch (e) {
      console.warn("feedback failed:", e);
    }
  }

  const references = answerObj?.references || [];
  const cardClass = isActive ? "qa-card active" : "qa-card collapsed";

  return (
    <div className={cardClass} ref={ref}>
      <div className="qa-question">{question}</div>

      {isLoading ? (
        <div className="loading-indicator">
          <div className="spinner" />
          <span>Caut răspunsul…</span>
        </div>
      ) : (
        <div className={`qa-answer-container${isTyping ? " typing" : ""}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} breaks>
            {displayedText}
          </ReactMarkdown>
        </div>
      )}

      {!isLoading && !isTyping && displayedText && references.length > 0 && (
        <div className="qa-sources">
          <div className="sources-title">Surse oficiale ROGEN</div>
          <div className="references-row">
            {references.map((url, i) => (
              <ReferenceCard key={i} url={url} apiBase={apiBase} />
            ))}
          </div>
        </div>
      )}

      {!isLoading && (
        <div className="feedback-area">
          <button className={`thumb-button up ${feedback === "up" ? "active" : ""}`} onClick={() => handleFeedback("up")} aria-label="Util">
            <ThumbUp filled={feedback === "up"} />
          </button>
          <button className={`thumb-button down ${feedback === "down" ? "active" : ""}`} onClick={() => handleFeedback("down")} aria-label="Nu e util">
            <ThumbDown filled={feedback === "down"} />
          </button>
          {toast && <span className="feedback-toast">{toast}</span>}
        </div>
      )}
    </div>
  );
}
